import { TrialDuration, trialDurationMap } from '@/components/dtcPartnerships/shared/types';
import { CampaignConfig } from '@/store/campaignConfig/types';
import { Prices } from '@/store/prices/types';
import { getPrice, isValidPlan, PlanCadence } from '@/store/purchaseParams/types';
import { calmLogger } from '@/utils/calmLogger';

/**
 * Mirrors the admin app's `page_config` shape (`apps/campaign-builder/src/models/campaign.ts`)
 * field-for-field. The two apps don't share a workspace, so each hand-writes its own mirror
 * rather than importing across apps.
 */

export interface PageConfigImage {
	show?: boolean;
	url?: string;
}

/** A `PageConfigImage`'s url, or `undefined` if unset, blank, or explicitly hidden via `show: false`. */
export function resolveImageUrl(image: PageConfigImage | undefined): string | undefined {
	return image?.show !== false ? image?.url || undefined : undefined;
}

/** Toggles and asset URLs — locale-independent, so "change the hero image" stays one edit. */
export interface PageConfigSettings {
	product_logo?: PageConfigImage;
	partner_logo?: PageConfigImage;
	hero?: PageConfigImage;
	/** Not yet consulted below — would need to gate whether the payment screen can be skipped. */
	payment_page_visible?: boolean;
	payment_page_image_url?: string;
	/**
	 * Destinations for the success screen's CTA. Only the first entry that passes the allowlist in
	 * postRedemptionCta.ts is used; the schema carries no labels, so a second button could not be
	 * told apart from the first.
	 */
	post_redemption_deeplinks?: string[];
	/**
	 * Overrides `campaign_partners.support_email` in the invalid-code message. Not yet read by
	 * this app — the invalid-code error screen this would customize doesn't exist here yet.
	 */
	error_contact_email?: string;
}

/** Team-authored marketing copy, per locale. Fixed page chrome is react-intl's job, not this. */
export interface PageConfigContent {
	header?: string;
	subheader?: string;
	descriptions?: string[];
	footer_prefix?: string;
	footer_suffix?: string;
	payment_page_text?: string;
	success_text?: string;
}

/**
 * Every leaf optional by design — the same schema validates a legacy, partially-populated
 * campaign and a freshly-launched one. `content` is keyed by locale.
 */
export interface PageConfig {
	settings?: PageConfigSettings;
	content?: Record<string, PageConfigContent>;
	default_locale?: string;
}

/**
 * Narrows the untyped wire payload to `PageConfig`. Not a schema validator — a wrong-shaped
 * value on a known key just degrades to `undefined`-like access on read. `raw` itself not being
 * an object is the one case that doesn't degrade gracefully (a legacy campaign can have
 * `page_config: null`), so that's the one case guarded explicitly.
 */
export function readPageConfig(raw: Record<string, unknown> | null | undefined): PageConfig {
	return raw && typeof raw === 'object' ? (raw as PageConfig) : {};
}

/**
 * `CampaignConfig.trial_duration` is a bare `string | null` on the wire (no enum on the
 * public response), but every existing consumer of trial length (`trialDurationMap`) is
 * keyed by the `TrialDuration` enum — narrow once here rather than at each call site.
 */
function resolveTrialDuration(value: string | null | undefined): TrialDuration | null {
	if (!value) return null;
	return (Object.values(TrialDuration) as string[]).includes(value) ? (value as TrialDuration) : null;
}

export interface TrialInfo {
	duration: TrialDuration | null;
	length: number;
	unit: 'days' | 'months' | null;
}

/** Resolves trial length/unit once for both FlowConfig's freeTrial* fields and the offer-summary text. */
export function resolveTrialInfo(value: string | null | undefined): TrialInfo {
	const duration = resolveTrialDuration(value);
	if (!duration) return { duration: null, length: 0, unit: null };
	return {
		duration,
		length: trialDurationMap[duration].duration,
		unit: trialDurationMap[duration].unit ?? null,
	};
}

export interface CampaignPurchaseFields {
	plan: PlanCadence;
	promotion: string;
	coupon: null;
	hasFreeTrial: boolean;
	freeTrialLength: number;
	freeTrialLengthUnit: 'days' | 'months' | null;
}

// Keyed by the CampaignConfig object itself, not campaign.id -- this only needs to dedupe the
// warn below across the handful of calls sharing one already-fetched campaign object within a
// single request/render (getServerSideProps calls this via both shapeCampaignForParams and
// shapeCampaignPricesForDisplay), not across campaigns or requests. A WeakMap lets each entry
// get collected once its campaign object does, so this can't leak or go stale across requests.
const resolvedPlanCache = new WeakMap<CampaignConfig, PlanCadence>();

/**
 * The SKU catalog is annual-only today, but plan_sku is admin-authored free text on the
 * write side (see campaign-fields.ts), not a real enum -- shapeCampaignForParams and
 * shapeCampaignPricesForDisplay must agree on this fallback, or the displayed discounted
 * price and the actually-purchased plan could disagree.
 */
function resolveCampaignPlan(campaign: CampaignConfig): PlanCadence {
	const cached = resolvedPlanCache.get(campaign);
	if (cached) return cached;

	const planSku = campaign.plan_sku ?? '';
	let plan: PlanCadence;
	if (isValidPlan(planSku)) {
		plan = planSku;
	} else {
		// Falling back to 'yearly' charges the wrong plan for a real campaign, silently.
		calmLogger.warn('Unrecognized campaign plan_sku, falling back to yearly', {
			campaignId: campaign.id,
			planSku,
		});
		plan = 'yearly';
	}
	resolvedPlanCache.set(campaign, plan);
	return plan;
}

/**
 * The `plan`/`promotion`/coupon/trial fields every other SimplifiedSignup flow's static config
 * feeds into `getInitialServerState` via `shapeConfigForParams` so Redux's `purchaseParams` (and
 * therefore checkout) reflects the route, not just the displayed copy. Campaigns have no static
 * config to do that with client-side — this is the same derivation, called server-side once the
 * campaign is fetched. Takes the already-resolved `trial` rather than re-deriving it, since
 * every caller needs it for other fields too. Typed narrower than `RouteSpecificPurchaseParams`
 * (which this still satisfies) so it can also spread directly into `FlowConfig`, whose matching
 * fields aren't nullable.
 */
export function shapeCampaignForParams(campaign: CampaignConfig, trial: TrialInfo): CampaignPurchaseFields {
	return {
		plan: resolveCampaignPlan(campaign),
		promotion: campaign.slug,
		// No campaign-level Stripe coupon id on the public view — the discount is applied
		// server-side during purchase, not derived here.
		coupon: null,
		hasFreeTrial: Boolean(trial.duration),
		freeTrialLength: trial.length,
		freeTrialLengthUnit: trial.unit,
	};
}

/**
 * shapeCampaignForParams sets coupon: null since there's no public Stripe coupon id, so the
 * pricing fetch (which only ever takes a coupon) can't discount prices.current itself. This
 * fills that gap for display purposes only, from the public percent_off field -- PurchaseDetails
 * reads prices.current for the charged-amount display and prices.coupons[plan].percent_off for
 * the discount badge. The real discount is still applied server-side at purchase time via
 * campaigns.stripe_coupon_id, independent of this.
 */
export function shapeCampaignPricesForDisplay(campaign: CampaignConfig, prices: Prices): Prices {
	const percentOff = campaign.percent_off;
	if (!percentOff) return prices;

	const plan = resolveCampaignPlan(campaign);
	const discountedPrice = Math.round(getPrice(plan, prices.original) * (1 - percentOff / 100));

	// Mirrors the campaign's own discount_duration_type rather than hardcoding 'once', so a
	// future reader of this coupon doesn't trust a value that's wrong for every 'repeating'
	// (first-z-years) campaign. Still inert today -- nothing reads a synthetic coupon's
	// duration -- and the shared Coupon type has nowhere to carry discount_duration_years,
	// so a 'repeating' campaign's terms copy doesn't yet say how many years it lasts.
	const duration = campaign.discount_duration_type === 'lifetime' ? 'forever' : 'repeating';

	return {
		...prices,
		current: { ...prices.current, [plan]: discountedPrice },
		coupons: {
			...prices.coupons,
			[plan]: { id: 'campaign-display-only', duration, valid: true, percent_off: percentOff },
		},
	};
}
